import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AccountBalance from '@mui/icons-material/esm/AccountBalance';
import AssignmentTurnedIn from '@mui/icons-material/esm/AssignmentTurnedIn';
import Build from '@mui/icons-material/esm/Build';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import Construction from '@mui/icons-material/esm/Construction';
import FieldModeIcon from '@mui/icons-material/esm/PhonelinkSetup';
import RefreshIcon from '@mui/icons-material/esm/Refresh';
import Schedule from '@mui/icons-material/esm/Schedule';
import Warning from '@mui/icons-material/esm/Warning';
import { RootState } from '../../store';
import { GaugeChart } from '../../components/charts';
import { PageHeader } from '../../components/layout';
import kpiService, { DashboardData } from '../../services/api/kpiService';
import changeRequestsService, { ChangeRequest } from '../../services/api/changeRequestsService';
import evmService from '../../services/api/evmService';
import { healthStandingFor, numericKpiValue } from './dashboardDisplay';

type PanelTone = 'error' | 'warning' | 'success' | 'info';

function money(value: number) {
  return `J$${Math.round(value).toLocaleString()}`;
}

function indexDisplay(value: number | null) {
  return value === null ? 'Awaiting baseline' : value.toFixed(2);
}

function percentDisplay(value: number | null, emptyLabel = 'Not set') {
  return value === null ? emptyLabel : `${value}%`;
}

function StatusPanel({
  title,
  value,
  detail,
  tone = 'info',
}: {
  title: string;
  value: string;
  detail: string;
  tone?: PanelTone;
}) {
  const colorMap: Record<PanelTone, string> = {
    error: 'error.main',
    warning: 'warning.main',
    success: 'success.main',
    info: 'primary.main',
  };

  return (
    <Box
      sx={{
        p: 2.25,
        height: '100%',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: colorMap[tone], mb: 0.75 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {detail}
      </Typography>
    </Box>
  );
}

export default function DashboardPage() {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [pendingChangeApprovals, setPendingChangeApprovals] = useState<ChangeRequest[]>([]);
  const [latestSpi, setLatestSpi] = useState<number | null>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await kpiService.getDashboardData();
      setDashboardData(data);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingChangeApprovals = async () => {
    try {
      setPendingChangeApprovals(await changeRequestsService.pendingApprovals());
    } catch (err) {
      console.error('Failed to load pending change approvals:', err);
      setPendingChangeApprovals([]);
    }
  };

  const fetchScheduleHealth = async () => {
    if (!activeProjectId) {
      setLatestSpi(null);
      return;
    }

    try {
      const series = await evmService.getSeries(activeProjectId);
      const spiPoints = series
        .map((point) => (point.spi === null ? null : Number(point.spi)))
        .filter((value): value is number => value !== null && Number.isFinite(value));
      setLatestSpi(spiPoints.length > 0 ? spiPoints[spiPoints.length - 1] : null);
    } catch (err) {
      console.error('Failed to load schedule health:', err);
      setLatestSpi(null);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchPendingChangeApprovals();
  }, []);

  useEffect(() => {
    fetchScheduleHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !dashboardData) {
    return (
      <Box>
        <PageHeader title="Project Dashboard" />
        <Alert severity="error">{error || 'No data available'}</Alert>
      </Box>
    );
  }

  const {
    healthScore,
    kpis = [],
    topRisks = [],
    criticalPathAlerts = [],
  } = dashboardData;
  const safeKpis = Array.isArray(kpis) ? kpis : [];
  const safeTopRisks = Array.isArray(topRisks) ? topRisks : [];
  const safeCriticalPathAlerts = Array.isArray(criticalPathAlerts) ? criticalPathAlerts : [];
  const kpiByName = safeKpis.reduce(
    (acc, entry) => ({ ...acc, [entry.name]: entry }),
    {} as Record<string, typeof safeKpis[number] | undefined>
  );

  const cashPositionVal = numericKpiValue(kpiByName.cashPosition, 0) ?? 0;
  const constructionProgressVal = numericKpiValue(kpiByName.constructionProgress);
  const cpiVal = numericKpiValue(kpiByName.cpi);
  const qualityStatusVal = numericKpiValue(kpiByName.qualityStatus);
  const acceptedPendingCertVal = Number(kpiByName.acceptedPendingCertValue?.value ?? 0);
  const nextDrawdownVal = Number(kpiByName.nextDrawdownValue?.value ?? 0);
  const drawdownAwaitingPaymentVal = Number(kpiByName.drawdownAwaitingPayment?.value ?? 0);
  const drawdownAwaitingPaymentCount = Number(kpiByName.drawdownAwaitingPayment?.change ?? 0);
  const materialsBlockedCount = Number(kpiByName.materialsBlocked?.value ?? 0);
  const equipmentBlockedCount = Number(kpiByName.equipmentBlocked?.value ?? 0);
  const qsLinesPendingApprovalVal = Number(kpiByName.qsLinesPendingApproval?.value ?? 0);
  const drawdownItemsPendingApprovalVal = Number(kpiByName.drawdownItemsPendingApproval?.value ?? 0);
  const healthStanding = healthStandingFor(healthScore?.overall);

  const exceptions = [
    drawdownAwaitingPaymentCount > 0
      ? `${drawdownAwaitingPaymentCount} drawdown payment${drawdownAwaitingPaymentCount === 1 ? '' : 's'} waiting (${money(drawdownAwaitingPaymentVal)})`
      : null,
    materialsBlockedCount > 0 ? `${materialsBlockedCount} material blocker${materialsBlockedCount === 1 ? '' : 's'}` : null,
    equipmentBlockedCount > 0 ? `${equipmentBlockedCount} equipment blocker${equipmentBlockedCount === 1 ? '' : 's'}` : null,
    safeCriticalPathAlerts.length > 0 ? `${safeCriticalPathAlerts.length} critical-path alert${safeCriticalPathAlerts.length === 1 ? '' : 's'}` : null,
    pendingChangeApprovals.length > 0 ? `${pendingChangeApprovals.length} change request${pendingChangeApprovals.length === 1 ? '' : 's'} awaiting approval` : null,
    safeTopRisks.length > 0 && safeTopRisks[0].score >= 10 ? `Top risk: ${safeTopRisks[0].title}` : null,
  ].filter((item): item is string => Boolean(item));

  const moduleLinks = [
    { title: 'PM Dashboard', detail: 'Weekly blockers, gates, and readiness', to: '/dashboard/pm' },
    { title: 'Financials', detail: 'Drawdowns, payments, and cash flow detail', to: '/financials' },
    { title: 'Procurement', detail: 'Material status and supplier actions', to: '/procurement' },
    { title: 'Risk Register', detail: 'Risk owners, mitigation, and status history', to: '/risks' },
  ];

  return (
    <Box>
      <PageHeader
        title="Project Dashboard"
        subtitle={`Executive health and exceptions only. Last refreshed ${lastRefresh.toLocaleTimeString()}`}
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              component={RouterLink}
              to="/field"
              variant="outlined"
              size="small"
              startIcon={<FieldModeIcon />}
            >
              Field Mode
            </Button>
            <Tooltip title="Refresh dashboard">
              <IconButton onClick={fetchDashboardData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />

      {!healthStanding && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Baseline data is incomplete, so schedule and performance indicators are shown only when the source data is available.
        </Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} lg={5}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Project Health
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Overall standing for executive review.
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  color: healthStanding?.color ?? 'text.secondary',
                  fontWeight: 700,
                }}
              >
                {healthStanding?.label ?? 'Awaiting baseline'}
              </Typography>
            </Stack>
            <Box sx={{ mt: 2.5 }}>
              <GaugeChart
                value={healthScore?.overall ?? 0}
                max={100}
                label="Overall Health"
                color={healthScore?.overall === undefined ? '#0288d1' : healthScore.overall >= 80 ? '#2e7d32' : healthScore.overall >= 60 ? '#ed6c02' : '#d32f2f'}
                height={220}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Warning color={exceptions.length > 0 ? 'warning' : 'success'} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Critical Exceptions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Only items that need management attention stay on this screen.
                </Typography>
              </Box>
            </Stack>
            {exceptions.length === 0 ? (
              <Alert severity="success">No critical exceptions are currently surfaced.</Alert>
            ) : (
              <Stack spacing={1.25}>
                {exceptions.map((exception) => (
                  <Box key={exception} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                    <Warning color="warning" fontSize="small" sx={{ mt: 0.25 }} />
                    <Typography variant="body2">{exception}</Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatusPanel
            title="Drawdown Readiness"
            value={nextDrawdownVal > 0 ? money(nextDrawdownVal) : 'No drawdown queued'}
            detail={`${money(acceptedPendingCertVal)} certified and pending next step`}
            tone={drawdownAwaitingPaymentCount > 0 ? 'warning' : 'success'}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatusPanel
            title="Schedule"
            value={indexDisplay(latestSpi)}
            detail={`${safeCriticalPathAlerts.length} critical-path alert${safeCriticalPathAlerts.length === 1 ? '' : 's'}`}
            tone={safeCriticalPathAlerts.length > 0 ? 'warning' : 'success'}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatusPanel
            title="Money"
            value={money(cashPositionVal)}
            detail={`CPI ${indexDisplay(cpiVal)}`}
            tone={cpiVal !== null && cpiVal < 0.95 ? 'warning' : 'info'}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatusPanel
            title="Quality & Safety"
            value={percentDisplay(qualityStatusVal, 'No open score')}
            detail={`Progress ${percentDisplay(constructionProgressVal)}`}
            tone={qualityStatusVal !== null && qualityStatusVal < 80 ? 'warning' : 'success'}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={5}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <AssignmentTurnedIn color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                This Week's Decisions
              </Typography>
            </Stack>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', gap: 1.25 }}>
                <AccountBalance color="primary" fontSize="small" />
                <Typography variant="body2">
                  Approve or reject {qsLinesPendingApprovalVal + drawdownItemsPendingApprovalVal} certification/drawdown item{qsLinesPendingApprovalVal + drawdownItemsPendingApprovalVal === 1 ? '' : 's'}.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.25 }}>
                <Construction color="primary" fontSize="small" />
                <Typography variant="body2">
                  Clear {materialsBlockedCount + equipmentBlockedCount} resource blocker{materialsBlockedCount + equipmentBlockedCount === 1 ? '' : 's'} before they hit the look-ahead plan.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.25 }}>
                <Schedule color="primary" fontSize="small" />
                <Typography variant="body2">
                  Review {safeCriticalPathAlerts.length} schedule exception{safeCriticalPathAlerts.length === 1 ? '' : 's'} with the PM.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Build color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Go To Detail
              </Typography>
            </Stack>
            <Grid container spacing={1.5}>
              {moduleLinks.map((link) => (
                <Grid item xs={12} sm={6} key={link.to}>
                  <Button
                    component={RouterLink}
                    to={link.to}
                    fullWidth
                    variant="outlined"
                    sx={{
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      textAlign: 'left',
                      p: 1.5,
                      height: '100%',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {link.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {link.detail}
                      </Typography>
                    </Box>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, color: 'text.secondary' }}>
        <CheckCircle color="success" fontSize="small" />
        <Typography variant="body2">
          Detailed tables, charts, sales history, resource logs, and recent activity are intentionally kept inside their source modules.
        </Typography>
      </Box>
    </Box>
  );
}
